import { S25Util } from "../../util/s25-util";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { Proto } from "../../pojo/Proto";
import { FileStoreUtil } from "../../util/filestore.util";
import { Item } from "../../pojo/Item";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { SeSecurityStore } from "./se.security.store";
import { RoleRights } from "./securityConfig/role.rights";
import { AccessLevel, AccessLevels, isMinFls } from "../../pojo/Fls";
import { S25Icons } from "../s25-icon/s25.icon.const";
import { Nullable } from "../../pojo/Util";
import { ENGAGEMENT_LABELS, SE_LOW_ACTIVITY_REPORT } from "./engagement-metrics/engagement.labels";
import { TrailLabels } from "./trails/trail.labels";
import { BudgetLabels } from "./budget/budget.labels";

/** Poll interval for SE attention indicators (sidebar workflow/forms, org service-hours tab). */
export const SE_ATTENTION_POLL_MS = 60_000;

/**
 * Extra SE nav checks beyond role rights / FLS flags on {@link RequiredRight}.
 * Add entries here and reference them via `requiredRight.seNavAccessRules`.
 */
export const SeNavAccessRules = {
    /** Full SE FLS, or at least one workflow todo pending action for the current user */
    fullFlsOrWorkflowAssignee: (store: InstanceType<typeof SeSecurityStore>) =>
        store.hasFLSOverride() || store.assignedWorkflowPendingCount() > 0,
    /** CHAT read FLS + SOC enabled (same gate as 25Live nav chat). */
    chatAccess: (store: InstanceType<typeof SeSecurityStore>) => store.canAccessChat(),
} as const;

export type SeNavAccessRule = keyof typeof SeNavAccessRules;

export type RequiredRight = {
    rights: RoleRights.RoleRightsList[];
    minAccessLevel?: AccessLevel;
    flsOverride?: boolean; // If true, Full FLS access bypasses role rights check
    requiresFullFls?: boolean; // If true, ONLY Full FLS access is allowed (no role rights)
    requiresReadFls?: boolean; // If true, requires STUDENT_ENGAGEMENT FLS at least Read level (and user must be logged in)
    /** Applied with AND after read/full FLS checks; see {@link SeNavAccessRules} */
    seNavAccessRules?: readonly SeNavAccessRule[];
};

export enum ViewModeCategory {
    General = "general",
    Personal = "personal",
    Workflow = "workflow",
    Forms = "forms",
    Elections = "elections",
    Budget = "budget",
    ServiceHours = "serviceHours",
    Curricula = "curricula",
    Settings = "settings",
    Organization = "organization",
    Organizations = "organizations",
    Event = "event",
    Events = "events",
    Users = "users",
    Email = "email",
}

export interface CategoryConfig {
    id: ViewModeCategory;
    label: string;
    icon: S25Icons.Name;
    defaultExpanded?: boolean;
    sortOrder: number;
}

export const CategoryConfigs: Record<ViewModeCategory, CategoryConfig> = {
    [ViewModeCategory.General]: {
        id: ViewModeCategory.General,
        label: "General",
        icon: "home",
        defaultExpanded: true,
        sortOrder: 1,
    },
    [ViewModeCategory.Personal]: {
        id: ViewModeCategory.Personal,
        label: "Personal",
        icon: "user",
        defaultExpanded: true,
        sortOrder: 2,
    },
    [ViewModeCategory.Organization]: {
        id: ViewModeCategory.Organization,
        label: "",
        icon: "organization",
        defaultExpanded: true,
        sortOrder: 3,
    },
    [ViewModeCategory.Event]: {
        id: ViewModeCategory.Event,
        label: "",
        icon: "calendarDate",
        defaultExpanded: true,
        sortOrder: 3,
    },
    [ViewModeCategory.Organizations]: {
        id: ViewModeCategory.Organizations,
        label: "Organizations",
        icon: "organization",
        defaultExpanded: false,
        sortOrder: 4,
    },
    [ViewModeCategory.Events]: {
        id: ViewModeCategory.Events,
        label: "Events",
        icon: "calendarDate",
        defaultExpanded: false,
        sortOrder: 5,
    },
    [ViewModeCategory.Users]: {
        id: ViewModeCategory.Users,
        label: "Users",
        icon: "user",
        defaultExpanded: false,
        sortOrder: 6,
    },
    [ViewModeCategory.Email]: {
        id: ViewModeCategory.Email,
        label: "Email",
        icon: "mailMail",
        defaultExpanded: false,
        sortOrder: 7,
    },
    [ViewModeCategory.Workflow]: {
        id: ViewModeCategory.Workflow,
        label: "Workflow",
        icon: "workflow",
        defaultExpanded: false,
        sortOrder: 8,
    },
    [ViewModeCategory.Forms]: {
        id: ViewModeCategory.Forms,
        label: "Forms",
        icon: "text",
        defaultExpanded: false,
        sortOrder: 9,
    },
    [ViewModeCategory.Elections]: {
        id: ViewModeCategory.Elections,
        label: "Elections",
        icon: "check",
        defaultExpanded: false,
        sortOrder: 10,
    },
    [ViewModeCategory.Budget]: {
        id: ViewModeCategory.Budget,
        label: "Budget",
        icon: "reports",
        defaultExpanded: false,
        sortOrder: 11,
    },
    [ViewModeCategory.ServiceHours]: {
        id: ViewModeCategory.ServiceHours,
        label: "Service Hours",
        icon: "clock",
        defaultExpanded: false,
        sortOrder: 11.5,
    },
    [ViewModeCategory.Curricula]: {
        id: ViewModeCategory.Curricula,
        label: TrailLabels.CURRICULA,
        icon: "bookmarkFilled",
        defaultExpanded: false,
        sortOrder: 11.75,
    },
    [ViewModeCategory.Settings]: {
        id: ViewModeCategory.Settings,
        label: "Settings",
        icon: "cogs",
        defaultExpanded: false,
        sortOrder: 12,
    },
};

export type ViewMode = {
    id: string;
    name: string;
    category?: ViewModeCategory;
    showByDefault?: boolean;
    iconProp?: S25Icons.Name;
    requiredRight?: RequiredRight;
    hasAccessFunction?: () => Promise<boolean>;
    subModes?: ViewMode[];
    sortOrder?: number;
    stateName?: string; // If specified, navigate to this state instead of seHome with mode parameter
    stateParams?: Record<string, string | number>;
};

export class StudentEngagementUtil {
    private static readonly ViewModesByCategory: Record<ViewModeCategory, ViewMode[]> = {
        [ViewModeCategory.General]: [
            {
                id: "home",
                name: "Dashboard",
                showByDefault: true,
                iconProp: "dashboard",
                sortOrder: 1,
                stateName: "seHomeView",
            },
            {
                id: "chat",
                name: "Chat",
                iconProp: "chat",
                sortOrder: 2,
                stateName: "seChat",
                requiredRight: {
                    rights: [],
                    requiresReadFls: true,
                    seNavAccessRules: ["chatAccess"],
                },
            },
        ],
        [ViewModeCategory.Personal]: [
            {
                id: "myForms",
                name: "My Forms",
                iconProp: "text",
                sortOrder: 1,
                stateName: "seMyForms",
                showByDefault: true,
                requiredRight: {
                    rights: [],
                    requiresReadFls: true,
                },
            },
            {
                id: "myWorkflow",
                name: "My Workflow",
                iconProp: "workflow",
                sortOrder: 2,
                stateName: "seMyWorkflow",
                requiredRight: {
                    rights: [],
                    requiresReadFls: true,
                    seNavAccessRules: ["fullFlsOrWorkflowAssignee"],
                },
            },
            {
                id: "myOrgs",
                name: "My Organizations",
                iconProp: "organization",
                sortOrder: 3,
                stateName: "seMyOrgs",
                requiredRight: {
                    rights: [],
                    requiresReadFls: true,
                },
            },
            {
                id: "myEvents",
                name: "My Events",
                iconProp: "calendarDate",
                sortOrder: 4,
                stateName: "seMyEvents",
                requiredRight: {
                    rights: [],
                    requiresReadFls: true,
                },
            },
            {
                id: "myHours",
                name: "My Service Hours",
                iconProp: "clock",
                sortOrder: 5,
                stateName: "seMyHours",
                requiredRight: {
                    rights: [],
                    requiresReadFls: true,
                },
            },
            {
                id: "electionNominations",
                name: "Election nominations",
                iconProp: "check",
                sortOrder: 5.5,
                stateName: "seElectionNominations",
                requiredRight: {
                    rights: [],
                    requiresReadFls: true,
                },
            },
            {
                id: "myActivity",
                name: ENGAGEMENT_LABELS.myActivityTitle,
                iconProp: "reports",
                sortOrder: 6,
                stateName: "seMyActivity",
                requiredRight: {
                    rights: [],
                    requiresReadFls: true,
                },
            },
            {
                id: "myTrails",
                name: TrailLabels.MY_PATHWAYS,
                iconProp: "bookmarkFilled",
                sortOrder: 6.25,
                stateName: "seMyTrails",
                requiredRight: {
                    rights: [],
                    requiresReadFls: true,
                },
            },
            {
                id: "userSettings",
                name: "User Settings",
                iconProp: "user",
                sortOrder: 6,
                stateName: "seUserSettings",
                requiredRight: {
                    rights: [],
                    requiresReadFls: true,
                },
            },
        ],
        [ViewModeCategory.Organization]: [
            {
                id: "hours",
                name: "Service Hours",
                iconProp: "clock",
                sortOrder: 1,
                stateName: "seOrganizationHours",
                requiredRight: {
                    rights: [RoleRights.RoleRightsList.SE_ORG_CONTACTS],
                    minAccessLevel: AccessLevels.Full,
                },
            },
        ],
        [ViewModeCategory.Event]: [],
        [ViewModeCategory.Organizations]: [
            {
                id: "orgSearch",
                name: "Search",
                iconProp: "search",
                sortOrder: 1,
                stateName: "seOrganizationsSearch",
                showByDefault: true,
            },
        ],
        [ViewModeCategory.Events]: [
            {
                id: "evSearch",
                name: "Search",
                iconProp: "search",
                sortOrder: 1,
                stateName: "seEventsSearch",
                showByDefault: true,
            },
        ],
        [ViewModeCategory.Users]: [
            {
                id: "userSearch",
                name: "Search",
                iconProp: "search",
                sortOrder: 1,
                stateName: "seUsersSearch",
                requiredRight: {
                    rights: [RoleRights.RoleRightsList.SE_USER_SEARCH],
                    minAccessLevel: AccessLevels.Read,
                    requiresReadFls: true,
                },
            },
        ],
        [ViewModeCategory.Workflow]: [
            {
                id: "workflow",
                name: "Workflow",
                iconProp: "workflow",
                sortOrder: 1,
                stateName: "seWorkflow",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "workflowRules",
                name: "Workflow Rules",
                iconProp: "text",
                sortOrder: 2,
                stateName: "seWorkflowRules",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "workflowActionHistory",
                name: "Workflow Action History",
                iconProp: "excel",
                sortOrder: 3,
                stateName: "seWorkflowActionHistory",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "workflowSubmissionAudit",
                name: "Submission Workflow Audit",
                iconProp: "excel",
                sortOrder: 4,
                stateName: "seWorkflowSubmissionAudit",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "groups",
                name: "Approval Groups",
                iconProp: "queue",
                stateName: "seGroups",
                sortOrder: 5,
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
        ],
        [ViewModeCategory.Forms]: [
            {
                id: "forms",
                name: "Forms List",
                iconProp: "text",
                sortOrder: 1,
                stateName: "seForms",
                requiredRight: {
                    rights: [RoleRights.RoleRightsList.SE_FORMS_MANAGE],
                    minAccessLevel: AccessLevels.Read,
                    flsOverride: true,
                },
            },
            {
                id: "submissionSearch",
                name: "Search",
                iconProp: "search",
                sortOrder: 2,
                stateName: "seSubmissionSearch",
                requiredRight: {
                    rights: [RoleRights.RoleRightsList.SE_FORMS_MANAGE],
                    minAccessLevel: AccessLevels.Read,
                    flsOverride: true,
                },
            },
            {
                id: "submissionAggregations",
                name: "Aggregations",
                iconProp: "reports",
                sortOrder: 3,
                stateName: "seSubmissionAggregations",
                requiredRight: {
                    rights: [RoleRights.RoleRightsList.SE_FORMS_MANAGE],
                    minAccessLevel: AccessLevels.Read,
                    flsOverride: true,
                },
            },
        ],
        [ViewModeCategory.Elections]: [
            {
                id: "electionAdmin",
                name: "Elections",
                iconProp: "check",
                sortOrder: 1,
                stateName: "seElectionAdmin",
                requiredRight: {
                    rights: [RoleRights.RoleRightsList.SE_ELECTION_MANAGE],
                    minAccessLevel: AccessLevels.Read,
                    flsOverride: true,
                },
            },
            {
                id: "electionResults",
                name: "Election Results",
                iconProp: "reports",
                sortOrder: 2,
                stateName: "seElectionResults",
                requiredRight: {
                    rights: [RoleRights.RoleRightsList.SE_ELECTION_MANAGE],
                    minAccessLevel: AccessLevels.Read,
                    flsOverride: true,
                },
            },
        ],
        [ViewModeCategory.Budget]: [
            {
                id: "budget",
                name: "Budget",
                iconProp: "reports",
                sortOrder: 1,
                stateName: "seBudget",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "pendingBudgetRequests",
                name: "Pending Requests",
                iconProp: "queue",
                sortOrder: 5,
                stateName: "sePendingBudgetRequests",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "budgetReports",
                name: BudgetLabels.BUDGET_REPORTS,
                iconProp: "reports",
                sortOrder: 6,
                stateName: "seBudgetReports",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "budgetCategories",
                name: BudgetLabels.BUDGET_CATEGORIES,
                iconProp: "cogs",
                sortOrder: 3,
                stateName: "seBudgetCategories",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "fiscalYears",
                name: "Fiscal Years",
                iconProp: "calendarDate",
                sortOrder: 4,
                stateName: "seFiscalYears",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
        ],
        [ViewModeCategory.ServiceHours]: [
            {
                id: "adminServiceHours",
                name: "All Service Hours",
                iconProp: "clock",
                sortOrder: 1,
                stateName: "seAdminServiceHours",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
        ],
        [ViewModeCategory.Curricula]: [
            {
                id: "trailAdmin",
                name: TrailLabels.CURRICULA,
                iconProp: "bookmarkFilled",
                sortOrder: 1,
                stateName: "seTrailAdmin",
                requiredRight: {
                    rights: [RoleRights.RoleRightsList.SE_CURRICULUM_MANAGE],
                    minAccessLevel: AccessLevels.Read,
                    flsOverride: true,
                },
            },
            {
                id: "trailExperiences",
                name: TrailLabels.REVIEW_EXPERIENCES,
                iconProp: "clipboard",
                sortOrder: 2,
                stateName: "seTrailExperiences",
                requiredRight: {
                    rights: [RoleRights.RoleRightsList.SE_CURRICULUM_MANAGE],
                    minAccessLevel: AccessLevels.Read,
                    flsOverride: true,
                },
            },
            {
                id: "trailAdminProgress",
                name: TrailLabels.STUDENT_PROGRESS,
                iconProp: "userCheck",
                sortOrder: 3,
                stateName: "seTrailAdminProgress",
                requiredRight: {
                    rights: [RoleRights.RoleRightsList.SE_CURRICULUM_MANAGE],
                    minAccessLevel: AccessLevels.Read,
                    flsOverride: true,
                },
            },
        ],
        [ViewModeCategory.Settings]: [
            {
                id: "config",
                name: "Configuration",
                iconProp: "cogs",
                sortOrder: 1,
                stateName: "studentEngagementConfig",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "emailTemplateSettings",
                name: "Email Template Settings",
                iconProp: "mailCompose",
                sortOrder: 1.5,
                stateName: "seEmailTemplateSettings",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "security",
                name: "Security",
                iconProp: "lock",
                sortOrder: 2,
                stateName: "studentEngagementSecurity",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "engagementDashboard",
                name: ENGAGEMENT_LABELS.reportTitle,
                iconProp: "reports",
                sortOrder: 3,
                stateName: "seEngagementDashboard",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "engagementAnalytics",
                name: "Engagement Analytics",
                iconProp: "reports",
                sortOrder: 3.5,
                stateName: "seEngagementAnalytics",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "engagementConfig",
                name: ENGAGEMENT_LABELS.configTitle,
                iconProp: "cogs",
                sortOrder: 4,
                stateName: "seEngagementConfig",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "badgeConfig",
                name: ENGAGEMENT_LABELS.badgesTitle,
                iconProp: "starFilled",
                sortOrder: 5,
                stateName: "seBadgeConfig",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "atRisk",
                name: SE_LOW_ACTIVITY_REPORT.displayName,
                iconProp: "warningYield",
                sortOrder: 6,
                stateName: "seAtRisk",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "seIdCardsConfig",
                name: ENGAGEMENT_LABELS.idCardsTitle,
                iconProp: "magneticStripeCard",
                sortOrder: 7,
                stateName: "seIdCardsConfig",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "seLayoutsConfig",
                name: ENGAGEMENT_LABELS.layoutsTitle,
                iconProp: "layout",
                sortOrder: 8,
                stateName: "seLayoutsConfig",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
        ],
        [ViewModeCategory.Email]: [
            {
                id: "email",
                name: "Compose",
                iconProp: "mailCompose",
                sortOrder: 1,
                stateName: "seEmail",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
            {
                id: "emailSent",
                name: "Sent",
                iconProp: "mailSent",
                sortOrder: 2,
                stateName: "seEmailSent",
                requiredRight: {
                    rights: [],
                    requiresFullFls: true,
                },
            },
        ],
    } as const;

    public static getViewModesByCategory(category: ViewModeCategory): readonly ViewMode[] {
        return this.ViewModesByCategory[category] || [];
    }

    public static getCategorizedViewModes(): Map<ViewModeCategory, readonly ViewMode[]> {
        const map = new Map<ViewModeCategory, ViewMode[]>();
        Object.entries(this.ViewModesByCategory).forEach(([category, modes]) => {
            map.set(
                <ViewModeCategory>category,
                modes.map((mode) => ({ ...mode, category: <ViewModeCategory>category })),
            );
        });
        return map;
    }

    /**
     * Check if a view mode is accessible based on user's permissions
     * @param mode The view mode to check
     * @param securityStore The security store instance to use for permission checks
     */
    public static async checkViewModeAccess(
        mode: ViewMode,
        securityStore: InstanceType<typeof SeSecurityStore>,
    ): Promise<boolean> {
        // Always show if marked as default
        if (mode.showByDefault) return true;

        // Use custom function if provided
        if (mode.hasAccessFunction) {
            return mode.hasAccessFunction();
        }

        if (mode.requiredRight) {
            await securityStore.ensureInitialized();
            return this.requiredRightAllowsAccess(mode.requiredRight, securityStore);
        }

        return false;
    }

    /**
     * Synchronous check: caller must have already called {@link SeSecurityStore.ensureInitialized}.
     */
    public static requiredRightAllowsAccess(
        rr: RequiredRight,
        securityStore: InstanceType<typeof SeSecurityStore>,
    ): boolean {
        const {
            rights,
            minAccessLevel,
            flsOverride = true,
            requiresFullFls = false,
            requiresReadFls = false,
            seNavAccessRules,
        } = rr;

        const hasFullFls = securityStore.hasFLSOverride();
        const hasReadFls = securityStore.hasReadFls();
        const maxRights = securityStore.maxRights();

        if (requiresReadFls && !hasReadFls) {
            return false;
        }
        if (requiresFullFls && !hasFullFls) {
            return false;
        }
        if (seNavAccessRules?.length && !this.evaluateSeNavAccessRules(securityStore, seNavAccessRules)) {
            return false;
        }
        if (flsOverride && hasFullFls) {
            return true;
        }
        if (rights.length) {
            if (!maxRights) {
                return false;
            }
            return rights.some((right) => {
                const actualAccess: AccessLevel = maxRights[right] || AccessLevels.None;
                return isMinFls(actualAccess, minAccessLevel || AccessLevels.Read);
            });
        }
        if (requiresReadFls && rights.length === 0) {
            return true;
        }
        return false;
    }

    public static evaluateSeNavAccessRules(
        securityStore: InstanceType<typeof SeSecurityStore>,
        rules: readonly SeNavAccessRule[] | undefined,
    ): boolean {
        if (!rules?.length) {
            return true;
        }
        return rules.every((rule) => SeNavAccessRules[rule](securityStore));
    }

    /**
     * Filter view modes based on user's permissions
     * @param modes Array of view modes to filter
     * @param securityStore The security store instance to use for permission checks
     * @returns Filtered array of accessible view modes
     */
    public static async filterViewModes(
        modes: readonly ViewMode[],
        securityStore: InstanceType<typeof SeSecurityStore>,
    ): Promise<ViewMode[]> {
        const results = await Promise.all(
            modes.map(async (mode) => {
                const hasAccess = await this.checkViewModeAccess(mode, securityStore);
                if (!hasAccess) return null;

                // Also filter subModes if they exist
                if (mode.subModes) {
                    const filteredSubModes = await this.filterViewModes(mode.subModes, securityStore);
                    return { ...mode, subModes: filteredSubModes.length > 0 ? filteredSubModes : undefined };
                }
                return mode;
            }),
        );

        return results.filter((mode): mode is ViewMode => mode !== null);
    }

    public static readonly LogoAttrIds: Partial<Record<Item.Ids, number>> = {
        [Item.Ids.Event]: CustomAttributes.system.HotEventImage.id,
        [Item.Ids.Organization]: CustomAttributes.system.StudentEngagementOrgLogo.id,
        [Item.Ids.Contact]: CustomAttributes.system.ContactProfileImage.id,
    } as const;

    public static formatDate(start: string, end: string, datePrefs: { date: string; dateTime: string; time: string }) {
        const { date, dateTime, time } = datePrefs;
        const startDate = S25Util.date.parseDropTZ(start);
        const endDate = S25Util.date.parseDropTZ(end);
        if (S25Util.date.equalDate(start, end)) {
            return `${S25Datefilter.transform(startDate, date)} ${S25Datefilter.transform(startDate, time)} - ${S25Datefilter.transform(endDate, time)}`;
        } else {
            return `${S25Datefilter.transform(startDate, dateTime)} - ${S25Datefilter.transform(endDate, dateTime)}`;
        }
    }

    public static parseUrl(url: Nullable<Proto.URLString>) {
        if (!url) return undefined;
        if (url.includes("://")) {
            return S25Util.replace25LiveUrl(url);
        } else if (FileStoreUtil.isFileStoreUrl(url)) {
            return FileStoreUtil.getImageUrl(url);
        }
        return undefined;
    }
}
